import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationErrors } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import BoardsShareScheduleFormComponent from 'webapp/components/boards/share/schedule/form';
import FormSelectBoardComponent from 'webapp/components/form/select-board';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';

import type { BoardShare } from 'webapp/models-custom';

type ValidationKeys = 'target' | 'deliveryOrRecipients';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: BoardShare;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
	onContentChange?: () => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormBoardShareMainComponent extends Component<MainComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	@tracked selectedItemId: string | null = null;

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);

		const model = this.args.model;
		let selected = '';
		if (model.loopId) {
			selected = `L${model.loopId}`;
		} else if (model.boardId) {
			selected = `B${model.boardId}`;
		}
		if (selected) {
			this.selectedItemId = selected;
		}
	}

	get itemsList() {
		const boardsList = sortBy(this.mmBoardsService.normalBoardModels, 'order', 'id').map(
			(item) => {
				const isDisabled = !item.canCurrentUserEdit,
					isFavorited = item.favorite;

				return {
					value: `B${item.id}`,
					label: item.name,
					label2: item.sourcesText,
					label3: this.getStatusIcon(isDisabled, isFavorited),
					avatar: item.imageMediumOrDefault,
					bgTheme: item.bgTheme,
					color: item.color,
					disabled: isDisabled,
					group: 'Dashboards',
				};
			},
		);

		const loopsList = sortBy(this.mmBoardLoopsService.normalBoardLoopModels, 'id').map(
			(item) => {
				const isDisabled = !item.canCurrentUserEdit;

				return {
					value: `L${item.id}`,
					label: item.title,
					label2: `<ui-icon name="Loop" size="sm"></ui-icon>${item.boards.length} dashboard${item.boards.length === 1 ? '' : 's'}`,
					label3: this.getStatusIcon(isDisabled, false),
					avatar: item.imageMediumOrDefault,
					bgTheme: item.bgTheme,
					color: item.color,
					disabled: isDisabled,
					group: 'Looped dashboards',
				};
			},
		);

		return boardsList.concat(loopsList);
	}

	@action
	changeSelectedItemId(selectedItemId: string) {
		this.selectedItemId = selectedItemId;

		const model = this.args.model;

		model.boardId = null;
		model.loopId = null;

		if (selectedItemId) {
			const selectedType = selectedItemId.startsWith('L') ? 'loopId' : 'boardId',
				selectedId = +selectedItemId.substr(1);
			model[selectedType] = selectedId;
		}
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.args.model;

		model.validate(); // to cleanup general errors

		if (!(model.loopId || model.boardId)) {
			errors.target = [{ message: 'Must choose a dashboard or loop!' }];
		}

		if (!model.isEmailDelivery && !model.isSlackDelivery) {
			// not shown in the form bcos of the custom fieldname
			// same errors will be handled by the sub-components
			// we need this here bcos form validation must fail
			errors.deliveryOrRecipients = [
				{
					message: 'At least one delivery type / recipients must be selected',
				},
			];
		}

		return errors;
	}

	@action
	save() {
		const model = this.args.model;

		model.cleanupUnusedScheduleValues();

		if (model.isNew) {
			const trackData = model.getTrackingDataForCreate(this.args.origin);

			this.analyticsService.event(TrackingEvent.SHARE_NEW_SNAPSHOT, trackData);
		}

		return model.save();
	}

	private getStatusIcon(isDisabled: boolean, isFavorited: boolean) {
		let statusText = '';

		if (isFavorited) {
			statusText += '<ui-icon name="StarFill" size="sm"></ui-icon>';
		}

		if (isDisabled) {
			statusText +=
				'<ui-icon name="Info" size="sm" title="Due to your user role, you don\'t have permissions to schedule snapshot for this dashboard"></ui-icon>';
		}

		return statusText;
	}

	<template>
		{{#if @model.isNew}}
			<div class="row row--board-selector">
				<FormSelectBoardComponent
					name="board"
					@selected={{this.selectedItemId}}
					@options={{this.itemsList}}
					@onChange={{this.changeSelectedItemId}}
					@placeholder="Select dashboard"
					@isNumeric={{false}}
					@isRequired={{true}}
					@extraClass="cs-image"
					@dropdownSearchPlaceholder="Search"
					@showFloatlabel={{false}}
					@tooltipOnSelectionFromProperty=""
					@isDisabled={{@submitInProgress}}
				/>
			</div>
		{{/if}}
		<BoardsShareScheduleFormComponent
			@model={{@model}}
			@isPeriodic={{true}}
			@errors={{@errors}}
			@trackingOrigin="notifications"
			@submitInProgress={{@submitInProgress}}
			@onContentChange={{@onContentChange}}
		/>
	</template>
}
